import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import type { IMgmSummaryValues } from "@/src/member-get-member/MainPage/MgmMainPage.d";
import { MgmSharedResources, MgmSharedResourcesEn } from "@/src/member-get-member/shared";
import MgmTransactionsSummaryModal from "@/src/member-get-member/shared/MgmTransactionsSummaryModal";
import WebViewNavigation from "@/src/member-get-member/shared/WebViewNavigation";
import { browserStatics } from "@/src/shared/BrowserStatic";
import PageLayout from "@/src/shared/page-layout";
import TelemetryButton from "@Components/Button/TelemetryButton/TelemetryButton";
import ImageLoader from "@Components/ImageLoader";
import {
    MgmEventType,
    TelemetryEntity,
    getCdnImage,
    gotoNativeScreen,
    urlFormat,
} from "@Components/Modules/Helpers";
import { scrollToCbBrandsList } from "@Constants/PortalConstants";
import { CashbackDetails } from "@Routes/CashbackLinks";

import type { ICashbackNumbersItem } from "../../cashback/LandingPage/CashbackNumbers";
import CashbackNumbers from "../../cashback/LandingPage/CashbackNumbers";
import PanelSection from "../../shared/panel-section";
import { MgmOffResources } from "./MgmOff.Resources";
import { MgmOffResourcesEn } from "./MgmOff.Resources.En";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./MgmOff.module.css";

export interface IMemberGetMember extends IMgmSummaryValues {
    mgmActiveDescription: boolean;
    isMobile: boolean;
}

const MgmOff: FC<IMemberGetMember> = (props) => {
    const navigate = useNavigate();
    const resources = languageSelector(MgmOffResources, MgmOffResourcesEn);
    const { mgmNumbersSummary } = languageSelector(MgmSharedResources, MgmSharedResourcesEn);
    const [openTrxModal, setOpenTrxModal] = useState(false);
    const offNumbers: ICashbackNumbersItem[] = [
        {
            numberInfo: `${props.mgmPendingBonusSum} zł`,
            description: mgmNumbersSummary.step1,
        },
        {
            numberInfo: `${props.mgmWithdrawnBonusSum + props.mgmApprovedBonusSum} zł`,
            description: mgmNumbersSummary.step2,
        },
        {
            numberInfo: `${props.mgmReceiverCount}`,
            description: mgmNumbersSummary.step3,
            buttonText: mgmNumbersSummary.openSummaryBtnLabel,
            button: {
                appearance: "transparentMagentaAppearance",
                onClick: () => setOpenTrxModal(true),
                telemetryCustom: {
                    eventName: `${TelemetryEntity.Cashback}-${MgmEventType.DashboardSummary}`,
                    actionType: "Click",
                },
            },
        },
    ];

    useEffect(
        () =>
            sendTelemetryEvent({
                eventName: `${TelemetryEntity.Cashback}-${MgmEventType.OffPage}`,
                actionType: "View",
            }),
        [],
    );

    return (
        <PageLayout isNewGrid breadcrumbs={props.isMobile && [{ labelKey: "mgm" }]}>
            {browserStatics.isMobileApp && <WebViewNavigation />}
            <div className={css.memberGetMemberComponent}>
                <div className={portalCss.standardContainerV2}>
                    <h1
                        className={`${css.title} ${portalCss.setSectionColOne} ${portalCss.h1Font}`}
                        dangerouslySetInnerHTML={{ __html: resources.title }}
                    />
                    <p
                        className={`${css.subTitle} ${portalCss.setSectionColOne} ${portalCss.h4Font}`}
                        dangerouslySetInnerHTML={{
                            __html: props.mgmActiveDescription
                                ? resources.mgmActiveDescription
                                : resources.description,
                        }}
                    />
                </div>

                {props.isSummaryVisible && (
                    <PanelSection title={mgmNumbersSummary.title}>
                        <CashbackNumbers
                            numbersList={offNumbers}
                            telemetryCustom={{
                                eventName: `${TelemetryEntity.Cashback}-${MgmEventType.DashboardSummary}`,
                                actionType: "View",
                            }}
                        />
                    </PanelSection>
                )}

                <div className={portalCss.standardContainerV2}>
                    <div
                        className={`${css.recommendAndEarn} ${portalCss.setSectionColOne} ${portalCss.containerMargins}`}
                    >
                        <p dangerouslySetInnerHTML={{ __html: resources.recommendAndEarn.title }} />
                        <ImageLoader src={getCdnImage(process.env.MGM_MainImg)} alt="Policaj i zarabiaj" />
                        <div className={css.detailsWrapper}>
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: resources.recommendAndEarn.info,
                                }}
                            />
                            <TelemetryButton
                                appearance="magentaRoundAppearance"
                                telemetryCustom={{
                                    eventName: `${TelemetryEntity.Cashback}-${MgmEventType.DashboardShopping}`,
                                    actionType: "Click",
                                }}
                                onClick={() => {
                                    const goToUrl = urlFormat(
                                        CashbackDetails,
                                        { [scrollToCbBrandsList]: true },
                                        true,
                                    );

                                    gotoNativeScreen(goToUrl);
                                    navigate(goToUrl);
                                }}
                            >
                                {resources.recommendAndEarn.btnText}
                            </TelemetryButton>
                        </div>
                    </div>
                </div>

                {openTrxModal && <MgmTransactionsSummaryModal />}
            </div>
        </PageLayout>
    );
};

export { MgmOff };
